import React from "react";
import { WidthProps } from "styled-system";
import { BaroSatisfaction } from "../../types/nps";

import BaseDashboardCard from "../BaseDashboardCard";
import Box from "../Box";
import styled from "styled-components";
import { CText } from "@carrefour/design-system-components-react";
import { Choice } from "../../types/commercial";
import { useSearchParams } from "react-router-dom";
import { SingleValue } from "react-select";
import SelectOption from "../SelectOption";
import useBreakPoint from "../../utils/useBreakPoint";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

const Table = styled.table`
  width: 100%;
`;
const Tr = styled.tr``;
const Th = styled.th<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;
const Text = styled(CText)`
  font-family: system-ui;
`;
const TextCustom = styled(CText)<{ $myColor: string }>`
  color: ${(props) => props?.$myColor} !important;
  font-family: system-ui;
`;
const BoxSelect = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;
const getColor = (value: number) => {
  if (value < 0) {
    return ["#262626", "white"];
  }
  if (value < 50) {
    return ["redLight", "#AA0D11"];
  }
  if (value < 100) {
    return ["grey1", "#575757"];
  }
  if (value < 150) {
    return ["greenLight", "#33691E"];
  }
  return ["greenDark", "white"];
};

type BarometerCardProps = {
  title: string;
  scoreData: Array<BaroSatisfaction>;
  isUnavailable: boolean;
  isLoading: boolean;
  options: Choice[];
  param: string;
  openInformationModal: () => void;
} & WidthProps;

const BarometerCard = ({
  title,
  scoreData,
  options,
  param,
  openInformationModal,
  ...rest
}: BarometerCardProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isBreakpoint = useBreakPoint();
    const { setKpiRange, getKpiRange } = useRange();
  
  const firstOptionValue = options?.[0]?.value;
  const valueParam = searchParams.get(param) ?? firstOptionValue;
  const getDefaultValueParams = () => {
    if (!firstOptionValue) {
      return undefined;
    }

    const value = getKpiRange(param) ?? firstOptionValue;
    if (!value) {
      return value;
    }
    searchParams.set(param, value);
    setSearchParams(searchParams);
    // tracking first
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: value,
    });
    return value;
  };

  const npsParam = searchParams.get(param) ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value;

    if (!newValue) {
      return;
    }

    if (newValue === npsParam) {
      return;
    }
    searchParams.set(param, newValue as string);
    setSearchParams(searchParams);
    setKpiRange(param, newValue as Range);
    // tracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };
  const listFiltre = scoreData?.filter(
    (score) => score?.questionCode !== "PC_satisfaction_generale"
  );

  const {t} = useTranslation();

  return (
    <BaseDashboardCard
      title={title}
      openInformationModal={openInformationModal}
      size="big"
      hasModal={true}
      importedDateTime={scoreData?.[0]?.importedDateTime}
      option={
        valueParam ? (
          <BoxSelect py={1}>
            <SelectOption
              list={options}
              value={valueParam}
              handleChange={handleChange}
            />
          </BoxSelect>
        ) : undefined
      }
      {...rest}
    >
      <Box>
        <Table>
          <thead>
            <Tr>
              <Th></Th>
              <Th $textAlign={"center"}>
                <Text bold size={"S"} tagStyle={"p"}>
                  {t('client.base')}
                </Text>
              </Th>
              <Th $textAlign="center">
                <Text bold size={"S"} tagStyle={"p"}>
                  {t('client.score')}
                </Text>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {listFiltre &&
              listFiltre?.map((score) => {
                const color = getColor(score?.averageRating);
                return (
                  <Tr key={"Baro" + score?.questionCode}>
                    <Td $textAlign="left" width={isBreakpoint ? "66%" : "50%"}>
                      <Text>{score?.questionLabel}</Text>
                    </Td>
                    <Td $textAlign="center">
                      <Text>
                        {new Intl.NumberFormat("fr-FR", {
                          maximumFractionDigits: 0,
                        }).format(score?.totalAnswers)}
                      </Text>
                    </Td>
                    <Td $textAlign="center">
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignContent={"center"}
                      >
                        <Box
                          bg={color?.[0]}
                          width={6}
                          py={"6px"}
                          borderRadius={"4px"}
                        >
                          <TextCustom
                            $myColor={color?.[1] as string}
                            bold
                            data-testid="color"
                          >
                            {new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 0,
                            }).format(score?.averageRating)}
                          </TextCustom>
                        </Box>
                      </Box>
                    </Td>
                  </Tr>
                );
              })}
          </tbody>
        </Table>
      </Box>
    </BaseDashboardCard>
  );
};

export default BarometerCard;
export { getColor };
